/**
 * StudyGroupsWidget Component
 *
 * Виджет для управления учебными группами организации.
 * Показывает список групп с расписанием, учениками и действиями.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Group,
  Text,
  Button,
  Box,
  Stack,
  ActionIcon,
  Menu,
  Loader,
  Center,
  Badge,
} from '@mantine/core';
import {
  IconCalendarTime,
  IconPlus,
  IconDots,
  IconPencil,
  IconTrash,
  IconUsers,
  IconClock,
  IconPlayerPause,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import BaseWidget from './BaseWidget';
import StudyGroupFormModal from './StudyGroupFormModal';
import StudyGroupViewModal from './StudyGroupViewModal';
import tokens from '../../../theme/designTokens';
import { useSession } from '../../../context/SessionContext';
import {
  getStudyGroups,
  deleteStudyGroup,
  togglePauseStudyGroup,
} from '../../../services/api/studyGroupsService';

const DAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

/**
 * Форматирование времени из строки "HH:MM:SS" → "HH:MM"
 */
const formatTime = (timeStr) => {
  if (!timeStr) return '';
  return timeStr.substring(0, 5);
};

/**
 * Компонент отображения одной группы
 */
const StudyGroupItem = ({ group, onView, onEdit, onDelete, onPause, canEdit, canDelete }) => {
  const scheduleText = (group.schedules || [])
    .map((s) => {
      let text = `${DAY_NAMES[s.day_of_week]} ${formatTime(s.start_time)}`;
      if (s.start_date) {
        text += ` (с ${new Date(s.start_date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}г)`;
      }
      return text;
    })
    .join(', ');

  return (
    <Group
      p="sm"
      style={{
        borderRadius: '12px',
        backgroundColor: tokens.colors.neutral[1],
        transition: 'background-color 150ms ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[2])}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[1])}
      onClick={() => onView(group)}
    >
      <Box
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: tokens.colors.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconCalendarTime size={20} color="white" />
      </Box>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500} truncate>
            {group.name}
          </Text>
          {group.subject_name && (
            <Badge size="xs" color="blue" variant="filled" radius="xl">
              {group.subject_name}
            </Badge>
          )}
        </Group>

        <Group gap="xs" mt={4}>
          {/* Расписание */}
          {scheduleText && (
            <Text size="xs" c="dimmed">
              <Group gap={4}>
                <IconClock size={12} />
                {scheduleText}
              </Group>
            </Text>
          )}
        </Group>

        <Group gap="md" mt={4}>
          {/* Педагог */}
          {group.teacher_name && (
            <Text size="xs" c="dimmed">
              {group.teacher_name}
            </Text>
          )}
          {/* Ученики */}
          <Text size="xs" c="dimmed">
            <Group gap={4}>
              <IconUsers size={12} />
              {group.student_count}{group.max_students ? `/${group.max_students}` : ''}
            </Group>
          </Text>
          {/* Продолжительность */}
          <Badge size="xs" color="gray" variant="filled" radius="xl">
            {group.duration_minutes} мин
          </Badge>
          {/* На паузе */}
          {!group.is_active && (
            <Badge size="xs" color="orange" variant="filled" radius="xl" leftSection={<IconPlayerPause size={10} />}>
              На паузе
            </Badge>
          )}
        </Group>
      </Box>

      {/* Меню действий */}
      {(canEdit || canDelete) && (
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" onClick={(e) => e.stopPropagation()}>
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
            {canEdit && (
              <Menu.Item
                leftSection={<IconPencil size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(group);
                }}
              >
                Редактировать
              </Menu.Item>
            )}
            {canEdit && (
              <Menu.Item
                leftSection={group.is_active ? <IconPlayerPause size={14} /> : <IconPlayerPlay size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onPause(group);
                }}
              >
                {group.is_active ? 'На паузу' : 'Возобновить'}
              </Menu.Item>
            )}
            {canDelete && (
              <>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconTrash size={14} />}
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(group);
                  }}
                >
                  Удалить
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
};

/**
 * Основной компонент виджета
 */
const StudyGroupsWidget = ({ organization, onActivityChange }) => {
  const { user } = useSession();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const canCreate = ['ADMIN', 'MANAGER'].includes(user?.role);
  const canEdit = ['ADMIN', 'MANAGER'].includes(user?.role);
  const canDelete = ['ADMIN', 'MANAGER'].includes(user?.role);

  // Модальное окно формы
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  // Модальное окно просмотра
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingGroup, setViewingGroup] = useState(null);

  // Загрузка групп
  const loadGroups = useCallback(async () => {
    if (!organization?.id) return;

    try {
      setLoading(true);
      const data = await getStudyGroups(organization.id, { pageSize: 100 });
      setGroups(data.items || []);
    } catch (err) {
      console.error('Failed to load study groups:', err);
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось загрузить учебные группы',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleAdd = () => {
    setEditingGroup(null);
    setFormModalOpen(true);
  };

  const handleView = (group) => {
    setViewingGroup(group);
    setViewModalOpen(true);
  };

  const handleEdit = (group) => {
    setViewModalOpen(false);
    setViewingGroup(null);
    setEditingGroup(group);
    setFormModalOpen(true);
  };

  const handlePause = async (group) => {
    try {
      await togglePauseStudyGroup(organization.id, group.id);
      setViewModalOpen(false);
      setViewingGroup(null);
      await loadGroups();
      if (onActivityChange) onActivityChange();
      notifications.show({
        title: 'Успешно',
        message: group.is_active ? 'Группа поставлена на паузу' : 'Группа возобновлена',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    }
  };

  const handleDelete = (group) => {
    modals.openConfirmModal({
      title: 'Удаление группы',
      centered: true,
      children: (
        <Text size="sm">
          Вы уверены, что хотите удалить группу «{group.name}»?
          Это действие нельзя отменить.
        </Text>
      ),
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteStudyGroup(organization.id, group.id);
          await loadGroups();
          if (onActivityChange) onActivityChange();
          notifications.show({
            title: 'Успешно',
            message: 'Группа удалена',
            color: 'green',
          });
        } catch (err) {
          notifications.show({
            title: 'Ошибка',
            message: err.message,
            color: 'red',
          });
        }
      },
    });
  };

  const handleFormSuccess = async () => {
    setFormModalOpen(false);
    setEditingGroup(null);
    await loadGroups();
    if (onActivityChange) onActivityChange();
  };

  const handleStudentChange = async () => {
    await loadGroups();
    if (onActivityChange) onActivityChange();
  };

  return (
    <>
      <BaseWidget
        title="Учебные группы"
        icon={IconCalendarTime}
        count={groups.length}
        collapsible={true}
        defaultExpanded={groups.length > 0}
        actions={
          canCreate && (
            <Button
              variant="light"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                handleAdd();
              }}
            >
              Добавить
            </Button>
          )
        }
      >
        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : groups.length > 0 ? (
          <Stack gap="xs">
            {groups.map((group) => (
              <StudyGroupItem
                key={group.id}
                group={group}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onPause={handlePause}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            ))}
          </Stack>
        ) : (
          <Box py="lg" style={{ textAlign: 'center' }}>
            <IconCalendarTime
              size={40}
              style={{ color: tokens.colors.neutral[4], marginBottom: '8px' }}
            />
            <Text size="sm" c="dimmed">
              Нет учебных групп
            </Text>
            {canCreate && (
              <Button
                variant="light"
                size="xs"
                mt="md"
                leftSection={<IconPlus size={14} />}
                onClick={handleAdd}
              >
                Создать группу
              </Button>
            )}
          </Box>
        )}
      </BaseWidget>

      {/* Модальное окно формы создания/редактирования */}
      <StudyGroupFormModal
        opened={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditingGroup(null);
        }}
        onSuccess={handleFormSuccess}
        group={editingGroup}
        organizationId={organization?.id}
      />

      {/* Модальное окно просмотра */}
      <StudyGroupViewModal
        opened={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingGroup(null);
        }}
        group={viewingGroup}
        organizationId={organization?.id}
        onEdit={canEdit ? handleEdit : null}
        onPause={canEdit ? handlePause : null}
        onStudentChange={handleStudentChange}
      />
    </>
  );
};

export default StudyGroupsWidget;
